import React from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import Spinner from '@/components/ui/Spinner';
import { AlertTriangle } from 'lucide-react';

interface InstitutionDisconnectDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  confirmDisconnect: boolean;
  setConfirmDisconnect: (next: boolean) => void;
  isDisconnecting: boolean;
  onConfirm: () => void;
  institutionName: string;
}

export default function InstitutionDisconnectDialog({
  open,
  setOpen,
  confirmDisconnect,
  setConfirmDisconnect,
  isDisconnecting,
  onConfirm,
  institutionName,
}: InstitutionDisconnectDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Disconnect {institutionName}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will remove all your saved investment accounts and holdings for {institutionName}.
            If any of those holdings are used in your portfolios, their allocations will also be removed.
            You can reconnect at any time to restore access.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex items-center space-x-2 py-4">
          <Checkbox
            id="confirm-disconnect"
            checked={confirmDisconnect}
            onCheckedChange={(checked) => setConfirmDisconnect(checked === true)}
          />
          <label
            htmlFor="confirm-disconnect"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I understand this will remove all saved data for {institutionName}
          </label>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setConfirmDisconnect(false)}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={!confirmDisconnect || isDisconnecting}
            variant="destructive"
            className="gap-2"
          >
            {isDisconnecting && <Spinner size="sm" ariaLabel="Disconnecting" />}
            {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
